// The publisher admin's remembered Station scope, in sessionStorage.
//
// CTV-759 / ADR-0036 keeps TWO values, and the distinction matters:
//
//   • ADMIN_SCOPE_KEY — the CURRENT scope. May be the `all` sentinel.
//   • LAST_ADMIN_STATION_KEY — the LAST SINGLE STATION. Never `all`. This is
//     the target a Scope collapse lands on when a viewer in All stations
//     opens a station-bound tab.
//
// They diverge only while scope is `all`; a collapse makes them the same
// station again. Its own module: exporting helpers from a component file
// breaks react-refresh.

export const LAST_ADMIN_STATION_KEY = 'ctvbuyer.admin.lastStation';
export const ADMIN_SCOPE_KEY = 'ctvbuyer.admin.scope';

/** The All-stations sentinel, as it appears in the URL's slug segment. */
export const ALL_STATIONS_SLUG = 'all';

export function rememberAdminStation(slug: string): void {
  // Never let the sentinel poison the collapse target. Without this,
  // /admin/all writes 'all' here and PublisherOnboardPage then calls
  // fetchPublisherConfig('all'), silently losing the sidebar chrome it
  // exists to provide.
  if (slug === ALL_STATIONS_SLUG) return;
  try {
    window.sessionStorage.setItem(LAST_ADMIN_STATION_KEY, slug);
  } catch {
    /* private mode; onboard just falls back to standalone chrome */
  }
}

export function rememberedAdminStation(): string | null {
  try {
    return window.sessionStorage.getItem(LAST_ADMIN_STATION_KEY);
  } catch {
    return null;
  }
}

/** Records the current Station scope: `all`, or a station slug. */
export function rememberAdminScope(scope: string): void {
  try {
    window.sessionStorage.setItem(ADMIN_SCOPE_KEY, scope);
  } catch {
    /* private mode; scope just doesn't persist across a reload */
  }
}

export function rememberedAdminScope(): string | null {
  try {
    return window.sessionStorage.getItem(ADMIN_SCOPE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clears both. Called on sign-out — without it a second sign-in in the same
 * tab inherits the previous user's station, which they may not even be able
 * to administer.
 */
export function clearAdminScopeMemory(): void {
  try {
    window.sessionStorage.removeItem(LAST_ADMIN_STATION_KEY);
    window.sessionStorage.removeItem(ADMIN_SCOPE_KEY);
  } catch {
    /* private mode; nothing was stored to begin with */
  }
}
